import AppGrid from "@/features/order-sheet-sets/app-grid";
import GridRowIndicator from "@/features/order-sheet-sets/grid-row-indicator";
import useInvoiceGrid from "@/features/invoices/hooks/use-invoice-grid";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";
import { DenseCreditInvoice } from "@/features/invoices/credit-invoices/credit-invoice.type";
import { useEffect, useMemo, useState } from "react";
import { CreditInvoiceRow } from "@/features/invoices/credit-invoices/credit-invoice-row.type";
import { creditInvoiceRowMapper } from "@/features/invoices/utils/credit-invoice-row-mapper";
import useIdentity from "@/features/ui/hooks/use-identity";

interface AgencyCreditInvoiceGridProps {
  resources?: DenseCreditInvoice[];
  mutateResources: () => void;
}

export default function CreditInvoiceGrid({
  resources,
  mutateResources,
}: AgencyCreditInvoiceGridProps) {
  const identity = useIdentity();
  const [rows, setRows] = useState<CreditInvoiceRow[]>();

  const rowMapper = useMemo(() => {
    return creditInvoiceRowMapper.copy();
  }, []);

  useEffect(() => {
    setRows((prev) => {
      return rowMapper.toRows(
        identity?.company?.type !== "BUYER"
          ? resources?.map((resource) => {
              return {
                ...resource,
                isRead: false,
              };
            })
          : resources,
        prev
      );
    });
  }, [resources, rowMapper, identity]);

  const invoiceGrid = useInvoiceGrid({
    rows,
    mutateRows: mutateResources,
  });

  const { navigate } = useRouterHelper();

  switch (identity?.company?.type) {
    case "AGENCY":
      return (
        <AppGrid
          rows={rows}
          metas={[
            {
              gap: "8px",
              rowType: "CreditInvoice",
              columns: [
                invoiceGrid.pairs.season.column,
                invoiceGrid.pairs.orderedBy.column,
                // invoiceGrid.pairs.issuedThrough.column,
                invoiceGrid.pairs.issuedBy.column,
                invoiceGrid.pairs.ownedBy.column,
                invoiceGrid.pairs.creditInvoiceType.column,
                invoiceGrid.pairs.title.column,
                invoiceGrid.pairs.creditReason.column,
                invoiceGrid.pairs.amount.column,
                invoiceGrid.pairs.issuedOn.column,
                invoiceGrid.pairs.creditUsage.column,
              ],
              fields: [
                invoiceGrid.pairs.season.field,
                invoiceGrid.pairs.orderedBy.field,
                // invoiceGrid.pairs.issuedThrough.field,
                invoiceGrid.pairs.issuedBy.field,
                invoiceGrid.pairs.ownedBy.field,
                invoiceGrid.pairs.creditInvoiceType.field,
                invoiceGrid.pairs.title.field,
                invoiceGrid.pairs.creditReason.field,
                invoiceGrid.pairs.amount.field,
                invoiceGrid.pairs.issuedOn.field,
                invoiceGrid.pairs.creditUsage.field,
              ],
              actions: [invoiceGrid.actions.downloadInvoice],
              onClick: (row) => {
                navigate(`/credit-invoices/${row.id}`);
              },
              indicator: (row) => (
                <GridRowIndicator type={"VIEW"} isHovered={row.isHovered} />
              ),
            },
          ]}
        />
      );
    case "BUYER":
      return (
        <AppGrid
          rows={rows}
          metas={[
            {
              gap: "8px",
              rowType: "CreditInvoice",
              columns: [
                invoiceGrid.pairs.season.column,
                // invoiceGrid.pairs.orderedBy.column,
                invoiceGrid.pairs.issuedThrough.column,
                invoiceGrid.pairs.issuedBy.column,
                invoiceGrid.pairs.creditInvoiceType.column,
                invoiceGrid.pairs.title.column,
                invoiceGrid.pairs.amount.column,
                invoiceGrid.pairs.issuedOn.column,
              ],
              fields: [
                invoiceGrid.pairs.season.field,
                // invoiceGrid.pairs.orderedBy.field,
                invoiceGrid.pairs.issuedThrough.field,
                invoiceGrid.pairs.issuedBy.field,
                invoiceGrid.pairs.creditInvoiceType.field,
                invoiceGrid.pairs.title.field,
                invoiceGrid.pairs.amount.field,
                invoiceGrid.pairs.issuedOn.field,
              ],
              actions: [invoiceGrid.actions.downloadInvoice],
              onClick: (row) => {
                navigate(`/credit-invoices/${row.id}`);
              },
              indicator: (row) => (
                <GridRowIndicator type={"VIEW"} isHovered={row.isHovered} />
              ),
            },
          ]}
        />
      );
    default:
      return null;
  }
}
